import { Link } from "react-router";
import { ArrowRight, BookOpen } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Main } from "@/components/shared/Main";
import { Header } from "@/components/shared/Header";
import { useApplicationContext } from "@/context/ApplicationContext";

interface Scenario {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  route: string;
  resource: string; // Permission resource
  action: string; // Permission action
  docsPath: string;
}

export default function LandingPage() {
  const { currentUser } = useAuth();
  const { documentationPagesForLandingsPage } = useApplicationContext();

  return (
    <>
      <Header />
      <Main>
        <div>
          {/* Header Section */}
          <div className="text-center mb-16">
            <div className="mb-6">
              <img
                src="/photonflow_logo.png"
                alt="Photon Flow Logo"
                className="h-16 w-16 mx-auto mb-6"
              />
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Welcome to Photon Flow{currentUser.name && `, ${currentUser.name.split(" ")[0]}`}
              </h1>
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Streamlining the photonics testing workflow in one comprehensive platform. Choose from
              the scenarios below to get started.
            </p>
          </div>

          {/* Scenarios Grid */}
          {documentationPagesForLandingsPage.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {documentationPagesForLandingsPage.map((scenario, index) => (
                <div
                  key={scenario.id}
                  className="animate-in fade-in slide-in-from-bottom-4 duration-500"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <ScenarioCard scenario={scenario} />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm max-w-2xl mx-auto">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Getting Started</h2>
                <p className="text-gray-600 mb-6">
                  Your account is being set up. Please contact your administrator to get access to
                  the features you need.
                </p>
                <p className="text-sm text-gray-500">
                  Current role: <span className="font-medium">{currentUser.role}</span>
                </p>
              </div>
            </div>
          )}

          {/* Additional Info Section - Only show if user has access to scenarios */}
          {documentationPagesForLandingsPage.length > 0 && (
            <div className="mt-20 text-center">
              <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm max-w-4xl mx-auto">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  Need Help Getting Started?
                </h2>
                <p className="text-gray-600 mb-6">
                  Each scenario includes detailed step-by-step documentation to guide you through
                  the process. Click the documentation link on any card to access comprehensive
                  guides, or check out our glossary for key terms and definitions.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button asChild variant="outline" className="group">
                    <Link to="/docs">
                      <BookOpen className="w-4 h-4 mr-2" />
                      View All Documentation
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                  <Button asChild variant="ghost" className="group">
                    <Link to="/docs/glossary#:~:text=Glossary">
                      <BookOpen className="w-4 h-4 mr-2" />
                      Glossary
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Main>
    </>
  );
}

interface ScenarioCardProps {
  scenario: Scenario;
}

function ScenarioCard({ scenario }: ScenarioCardProps) {
  const Icon = scenario.icon;

  return (
    <Card className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1 bg-white border-gray-200 relative overflow-hidden">
      {/* Background hover effect */}
      <div className="absolute inset-0 bg-linear-to-br from-blue-50/50 via-transparent to-gray-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      <CardHeader className="relative">
        <div className="flex items-center justify-between mb-4">
          <div className="w-12 h-12 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center group-hover:bg-blue-50 group-hover:border-blue-200 transition-colors duration-300">
            <Icon className="w-6 h-6 text-gray-600 group-hover:text-blue-600 transition-colors duration-300" />
          </div>
        </div>
        <CardTitle className="text-xl font-semibold text-gray-900 group-hover:text-gray-700 transition-colors">
          {scenario.title}
        </CardTitle>
      </CardHeader>

      <CardContent className="relative">
        <p className="text-gray-600 mb-6 leading-relaxed">{scenario.description}</p>

        <div className="flex flex-col gap-3">
          <Button asChild className="w-full group/btn">
            <Link to={scenario.route}>
              Get Started
              <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
            </Link>
          </Button>

          {scenario.docsPath && (
            <Button asChild variant="outline" size="sm" className="w-full group/docs">
              <Link to={scenario.docsPath}>
                <BookOpen className="w-4 h-4 mr-2" />
                Step-by-step Guide
                <ArrowRight className="w-3 h-3 ml-2 group-hover/docs:translate-x-1 transition-transform" />
              </Link>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
